import { FC } from "react";

import { Box, Stack, useTheme } from "@mui/material";

import Image from "@/components/Image/Image";
import { useDynamicThemeAwareVariables } from "@/features/FormV2/hooks/useDynamicThemeAwareVariables";
import { STEP_TO_ELEMENT_ID } from "@/features/LiveFormEditor/AnimatePreview/Arrow/ArrowContext";
import { FormBlock, FormBlockProps } from "@/forms-design-system/FormBlock/FormBlock";
import { FormTypography } from "@/forms-design-system/FormTypography/FormTypography";
import { mergeSx } from "@/helpers/mergeSx";
import { useMediaQuery } from "@/hooks/useMediaQuery";

type ThemedOrganizationLogoAndNameBlockProps = Omit<FormBlockProps, "ref" | "variant" | "data-test"> & {
  name: string;
  logoSrc?: string;
  withFallbackImage?: boolean;
};

export const ThemedOrganizationLogoAndNameBlock: FC<ThemedOrganizationLogoAndNameBlockProps> = ({
  name,
  logoSrc,
  sx,
  withFallbackImage = false,
  ...rest
}) => {
  const theme = useTheme();
  const { isSmallScreen } = useMediaQuery();

  const { fallbackImageBackgroundColor } = useDynamicThemeAwareVariables(({ mode }) => ({
    fallbackImageBackgroundColor:
      mode === "light" ? theme.palette.forms.surface.quiet : theme.palette.forms.surface.supershy,
  }));

  const logoSize = isSmallScreen ? 28 : 40;

  const styles = mergeSx(
    {
      display: "inline-flex",
      flexDirection: "row",
      alignItems: "center",
      gap: { xs: 1, md: 1.5 },
      padding: { xs: theme.spacing(0.5, 1), md: theme.spacing(1, 2) },
    },
    sx
  );

  const logoStyles = {
    borderRadius: theme.radius(0.5),
    width: logoSize,
    height: logoSize,
    objectFit: "contain",
  };

  return (
    <Stack>
      <FormBlock id={STEP_TO_ELEMENT_ID["logo"]} variant="card" data-test="form-v2-logo-chip" {...rest} sx={styles}>
        {logoSrc ? (
          <Image src={logoSrc} alt={name} width={80} height={80} sx={logoStyles} nextImage />
        ) : withFallbackImage ? (
          <Box
            width={logoSize}
            height={logoSize}
            sx={mergeSx(logoStyles, { backgroundColor: fallbackImageBackgroundColor })}
          />
        ) : null}
        <FormTypography
          variant={isSmallScreen ? "subtitle2" : "h6"}
          vibe="intense"
          sx={{
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {name}
        </FormTypography>
      </FormBlock>
    </Stack>
  );
};
